import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, FileText, Edit, Eye, Phone, FileCheck, Download, Trash2, Upload, ChevronDown, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import CustomerQuoteForm from "../crm/CustomerQuoteForm";
import { CompactDealForm } from "./CompactDealForm";
import BankSummaryReport from "./BankSummaryReport";
import RESPAComplianceForm from "./RESPAComplianceForm";
import DocumentChecklist from "./DocumentChecklist";
import ContractVerificationForm from "./ContractVerificationForm";
import { DealDocumentsWrapper } from "./DealDocumentsWrapper";
import { QuickUploadModal } from "./QuickUploadModal";
import * as XLSX from 'xlsx';

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  seller_email?: string;
  seller_phone?: string;
  seller_address?: string;
  buyer_name: string;
  buyer_address?: string;
  buyer_current_on_contract: boolean;
  buyer_payment_history?: string;
  date_of_sale?: string;
  first_payment_date?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  has_balloon_payment: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  property_type?: string;
  property_description?: string;
  property_address?: string;
  estimated_property_value?: number;
  current_balance?: number;
  payments_remaining?: number;
  offer_price?: number;
  offer_date?: string;
  status: string;
  bank_notes?: string;
  internal_notes?: string;
  zillow_link?: string;
  zillow_researched_at?: string;
  created_at: string;
  updated_at: string;
}

// Expandable table row component for compact view
interface DealTableRowProps {
  deal: Deal;
  formatCurrency: (amount: number | null | undefined) => string;
  onEdit: (deal: Deal) => void;
  onViewSummary: (deal: Deal) => void;
  onShowDocuments: (deal: Deal) => void;
  onShowRESPA: (deal: Deal) => void;
  onShowVerification: (deal: Deal) => void;
  onCreateQuote: (deal: Deal) => void;
  onDelete: (dealId: string, dealTitle: string) => void;
  onStatusChange: (dealId: string, newStatus: string) => void;
}

const DealTableRow = ({
  deal,
  formatCurrency,
  onEdit,
  onViewSummary,
  onShowDocuments,
  onShowRESPA,
  onShowVerification,
  onCreateQuote,
  onDelete,
  onStatusChange,
}: DealTableRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'new': return 'secondary';
      case 'quoted': return 'default';
      case 'Accepted Offer': return 'default';
      case 'denied offer': return 'destructive';
      case 'Closed': return 'secondary';
      case 'other': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setIsOpen(!isOpen)}>
        <TableCell className="p-2">
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="font-medium py-2">
          <div className="max-w-[200px] truncate" title={deal.deal_title}>
            {deal.deal_title}
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="max-w-[120px] truncate" title={deal.seller_name}>
            {deal.seller_name}
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center gap-1">
            <span className="max-w-[100px] truncate" title={deal.buyer_name}>
              {deal.buyer_name}
            </span>
            {!deal.buyer_current_on_contract && (
              <Badge variant="destructive" className="text-[10px] px-1 py-0">Behind</Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right py-2 tabular-nums">
          {formatCurrency(deal.offer_price)}
        </TableCell>
        <TableCell className="text-right py-2 tabular-nums">
          {formatCurrency(deal.current_balance)}
        </TableCell>
        <TableCell className="text-right py-2 tabular-nums">
          {formatCurrency(deal.payment_amount)}
        </TableCell>
        <TableCell className="py-2 text-xs text-muted-foreground">
          {new Date(deal.created_at).toLocaleDateString()}
        </TableCell>
        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
          <Select value={deal.status} onValueChange={(value) => onStatusChange(deal.id, value)}>
            <SelectTrigger className="h-7 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="Accepted Offer">Accepted</SelectItem>
              <SelectItem value="denied offer">Denied</SelectItem>
              <SelectItem value="Unbuyable">Unbuyable</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(deal)}>
              <Edit className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onViewSummary(deal)}>
              <FileText className="h-3 w-3" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {isOpen && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={10} className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => onEdit(deal)} className="h-7 text-xs">
                <Edit className="h-3 w-3 mr-1" />
                Edit Deal
              </Button>
              <Button size="sm" onClick={() => onViewSummary(deal)} className="h-7 text-xs">
                <FileText className="h-3 w-3 mr-1" />
                Bank Summary
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onShowDocuments(deal)} className="h-7 text-xs">
                <FileText className="h-3 w-3 mr-1" />
                Agreements & Docs
              </Button>
              <Button size="sm" variant="outline" onClick={() => onShowRESPA(deal)} className="h-7 text-xs">
                <FileCheck className="h-3 w-3 mr-1" />
                RESPA
              </Button>
              <Button size="sm" variant="outline" onClick={() => onShowVerification(deal)} className="h-7 text-xs">
                <FileCheck className="h-3 w-3 mr-1" />
                Verification
              </Button>
              {(deal.status === 'new' || deal.status === 'Accepted Offer') && (
                <Button size="sm" onClick={() => onCreateQuote(deal)} className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" />
                  Create Quote
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="destructive" className="h-7 text-xs">
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Deal</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{deal.deal_title}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(deal.id, deal.deal_title)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete Deal
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <div className="ml-auto text-xs text-muted-foreground">
                Created {new Date(deal.created_at).toLocaleDateString()}
                {deal.property_address && <span className="ml-2">• {deal.property_address}</span>}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const DealsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showRESPAForm, setShowRESPAForm] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showQuickUpload, setShowQuickUpload] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [sortByCreated, setSortByCreated] = useState<'asc' | 'desc' | null>(null);

  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchDeals();
    fetchCustomers();
  }, []);

  const fetchDeals = async () => {
    try {
      console.log('Fetching deals...');
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Deals query result:', { data, error });
      if (error) throw error;
      setDeals(data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast({
        title: "Error loading deals",
        description: "Please try again",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCreateDeal = () => {
    setSelectedDeal(null);
    setShowForm(true);
  };

  const handleEditDeal = (deal: Deal) => {
    setSelectedDeal(deal);
    setShowForm(true);
  };

  const handleViewSummary = async (deal: Deal) => {
    // Fetch full deal details for the summary
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('id', deal.id)
        .single();

      if (error) throw error;
      setSelectedDeal(data);
      setShowSummary(true);
    } catch (error) {
      console.error('Error fetching deal details:', error);
      toast({
        title: "Error loading deal details",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedDeal(null);
    fetchDeals();
  };

  const handleCreateQuoteFromDeal = (deal: Deal) => {
    setSelectedDeal(deal);
    setShowQuoteForm(true);
  };

  const handleDeleteDeal = async (dealId: string, dealTitle: string) => {
    try {
      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', dealId);

      if (error) throw error;

      toast({
        title: "Deal deleted",
        description: `"${dealTitle}" has been successfully deleted`,
      });

      fetchDeals(); // Refresh the list
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast({
        title: "Error deleting deal",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleQuoteCreated = () => {
    setShowQuoteForm(false);
    setSelectedDeal(null);
    toast({
      title: "Success",
      description: "Quote created successfully from deal",
    });
  };

  const exportToExcel = () => {
    const excelData = filteredDeals.map(deal => ({
      'Deal Title': deal.deal_title,
      'Seller Name': deal.seller_name,
      'Seller Email': deal.seller_email || '',
      'Seller Phone': deal.seller_phone || '',
      'Seller Address': deal.seller_address || '',
      'Buyer Name': deal.buyer_name,
      'Buyer Address': deal.buyer_address || '',
      'Buyer Current on Contract': deal.buyer_current_on_contract ? 'Yes' : 'No',
      'Date of Sale': deal.date_of_sale || '',
      'First Payment Date': deal.first_payment_date || '',
      'Sales Price': deal.sales_price || '',
      'Down Payment': deal.down_payment_amount || '',
      'Beginning Balance': deal.beginning_balance || '',
      'Current Balance': deal.current_balance || '',
      'Payment Amount': deal.payment_amount || '',
      'Interest Rate': deal.interest_rate || '',
      'Original Term (Months)': deal.original_term_months || '',
      'Payments Remaining': deal.payments_remaining || '',
      'Has Balloon Payment': deal.has_balloon_payment ? 'Yes' : 'No',
      'Balloon Amount': deal.balloon_amount || '',
      'Balloon Due Date': deal.balloon_due_date || '',
      'Property Type': deal.property_type || '',
      'Property Description': deal.property_description || '',
      'Property Address': deal.property_address || '',
      'Property Value': deal.estimated_property_value || '',
      'Status': deal.status,
      'Bank Notes': deal.bank_notes || '',
      'Internal Notes': deal.internal_notes || '',
      'Created Date': new Date(deal.created_at).toLocaleDateString(),
      'Updated Date': new Date(deal.updated_at).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Auto-adjust column widths
    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deals');

    const fileName = `deals-export-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast({
      title: "Export Complete",
      description: `Exported ${filteredDeals.length} deals to Excel`,
    });
  };

  const filteredDeals = deals
    .filter(deal => {
      const matchesSearch = 
        deal.deal_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.seller_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.buyer_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || deal.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // If sorting by created date explicitly
      if (sortByCreated) {
        const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        return sortByCreated === 'asc' ? diff : -diff;
      }

      // Default: bottom-tier statuses sink, then newest first
      const bottomStatuses = ['denied offer', 'Unbuyable', 'other'];
      const aIsBottom = bottomStatuses.includes(a.status);
      const bIsBottom = bottomStatuses.includes(b.status);
      
      if (aIsBottom && !bIsBottom) return 1;
      if (!aIsBottom && bIsBottom) return -1;
      
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const calculateTotalCashReceived = (deal: Deal) => {
    const downPayment = deal.down_payment_amount || 0;
    const monthlyPayment = deal.payment_amount || 0;
    const originalTerm = deal.original_term_months || 0;
    const paymentsRemaining = deal.payments_remaining || 0;
    
    // Calculate payments made = original term - payments remaining
    const paymentsMade = originalTerm - paymentsRemaining;
    const totalMonthlyPayments = Math.max(0, paymentsMade) * monthlyPayment;
    
    return downPayment + totalMonthlyPayments;
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'new': return 'secondary';
      case 'quoted': return 'default';
      case 'Accepted Offer': return 'default';
      case 'denied offer': return 'destructive';
      case 'Closed': return 'secondary';
      case 'other': return 'outline';
      default: return 'secondary';
    }
  };

  const handleStatusChange = async (dealId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('deals')
        .update({ status: newStatus })
        .eq('id', dealId);

      if (error) throw error;

      toast({
        title: "Status Updated",
        description: `Deal status changed to ${newStatus.replace('_', ' ')}`,
      });

      fetchDeals(); // Refresh the list
    } catch (error) {
      console.error('Error updating deal status:', error);
      toast({
        title: "Error updating status",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  if (showForm) {
    return (
      <CompactDealForm
        deal={selectedDeal}
        onClose={() => setShowForm(false)}
        onSuccess={handleFormSuccess}
      />
    );
  }

  if (showSummary && selectedDeal) {
    return (
      <BankSummaryReport
        deal={selectedDeal}
        onClose={() => setShowSummary(false)}
      />
    );
  }

  if (showQuoteForm && selectedDeal) {
    return (
      <CustomerQuoteForm
        customers={customers}
        selectedDeal={selectedDeal}
        onQuoteCreated={handleQuoteCreated}
        onCancel={() => {
          setShowQuoteForm(false);
          setSelectedDeal(null);
        }}
      />
    );
  }

  if (showRESPAForm && selectedDeal) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">RESPA Compliance Form</h1>
          <Button onClick={() => {
            setShowRESPAForm(false);
            setSelectedDeal(null);
          }}>
            Back to Deals
          </Button>
        </div>
        <RESPAComplianceForm
          dealId={selectedDeal.id}
          salesDate={selectedDeal.date_of_sale}
        />
      </div>
    );
  }

  if (showChecklist && selectedDeal) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Paperwork Checklist</h1>
          <Button onClick={() => {
            setShowChecklist(false);
            setSelectedDeal(null);
          }}>
            Back to Deals
          </Button>
        </div>
        <DocumentChecklist
          dealTitle={selectedDeal.deal_title}
          sellerName={selectedDeal.seller_name}
        />
      </div>
    );
  }

  if (showVerification && selectedDeal) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Contract Verification Form</h1>
          <Button onClick={() => {
            setShowVerification(false);
            setSelectedDeal(null);
          }}>
            Back to Deals
          </Button>
        </div>
        <ContractVerificationForm
          dealTitle={selectedDeal.deal_title}
          sellerName={selectedDeal.seller_name}
          buyerName={selectedDeal.buyer_name}
          buyerAddress={selectedDeal.buyer_address}
        />
      </div>
    );
  }

  if (showDocuments && selectedDeal) {
    return (
      <DealDocumentsWrapper
        deal={selectedDeal}
        onClose={() => {
          setShowDocuments(false);
          setSelectedDeal(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deals Management</h1>
          <p className="text-muted-foreground">
            Manage land contract deals and generate bank summaries
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowQuickUpload(true)}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Quick Upload
          </Button>
          <Button 
            variant="outline" 
            onClick={exportToExcel}
            className="flex items-center gap-2"
            disabled={filteredDeals.length === 0}
          >
            <Download className="h-4 w-4" />
            Export to Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin-portal/agreements')}
            className="flex items-center gap-2"
          >
            <FileCheck className="h-4 w-4" />
            Purchase Agreements
          </Button>
          <Button onClick={handleCreateDeal} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Deal
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search deals, sellers, or buyers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="Accepted Offer">Accepted Offer</SelectItem>
                <SelectItem value="denied offer">Denied Offer</SelectItem>
                <SelectItem value="Unbuyable">Unbuyable</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Deals Table */}
      {loading ? (
        <div className="text-center py-8">
          <p>Loading deals...</p>
        </div>
      ) : filteredDeals.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">
              {deals.length === 0 ? 'No deals created yet.' : 'No deals match your search criteria.'}
            </p>
            {deals.length === 0 && (
              <Button onClick={handleCreateDeal} className="mt-4">
                Create Your First Deal
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30px]"></TableHead>
                  <TableHead>Deal Title</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead className="text-right">Offer</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Payment</TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => setSortByCreated(prev => prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')}
                  >
                    Created {sortByCreated === 'desc' ? '↓' : sortByCreated === 'asc' ? '↑' : ''}
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeals.map((deal) => (
                  <DealTableRow
                    key={deal.id}
                    deal={deal}
                    formatCurrency={formatCurrency}
                    onEdit={handleEditDeal}
                    onViewSummary={handleViewSummary}
                    onShowDocuments={(deal) => {
                      setSelectedDeal(deal);
                      setShowDocuments(true);
                    }}
                    onShowRESPA={(deal) => {
                      setSelectedDeal(deal);
                      setShowRESPAForm(true);
                    }}
                    onShowVerification={(deal) => {
                      setSelectedDeal(deal);
                      setShowVerification(true);
                    }}
                    onCreateQuote={handleCreateQuoteFromDeal}
                    onDelete={handleDeleteDeal}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Quick Upload Modal */}
      <QuickUploadModal
        open={showQuickUpload}
        onOpenChange={setShowQuickUpload}
        deals={deals}
        onUploadComplete={fetchDeals}
      />
    </div>
  );
};

export default DealsPage;